/**
 * 二次元美女教师形象组件
 * 提供智能提示和指导功能
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  TouchableOpacity,
  Dimensions,
} from 'react-native';

const { width, height } = Dimensions.get('window');

interface TeacherAvatarProps {
  message?: string;
  onDismiss?: () => void;
  showTeacher?: boolean;
  position?: 'bottom-right' | 'bottom-left' | 'center';
}

const TeacherAvatar: React.FC<TeacherAvatarProps> = ({
  message = '',
  onDismiss,
  showTeacher = false,
  position = 'bottom-right',
}) => {
  const [fadeAnim] = useState(new Animated.Value(0));
  const [scaleAnim] = useState(new Animated.Value(0.8));
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (showTeacher && message) {
      setIsVisible(true);
      // 入场动画
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 500,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 150,
          friction: 8,
          useNativeDriver: true,
        }),
      ]).start();

      // 自动消失（8秒后）
      const timer = setTimeout(() => {
        hideTeacher();
      }, 8000);

      return () => clearTimeout(timer);
    } else {
      hideTeacher();
    }
  }, [showTeacher, message]);

  const hideTeacher = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setIsVisible(false);
      onDismiss?.();
    });
  };

  const getPositionStyle = () => {
    switch (position) {
      case 'bottom-left':
        return { bottom: 120, left: 20 };
      case 'center':
        return {
          top: height / 2 - 100,
          left: width / 2 - 100,
        };
      case 'bottom-right':
      default:
        return { bottom: 120, right: 20 };
    }
  };

  if (!isVisible) {return null;}

  return (
    <Animated.View
      style={[
        styles.container,
        getPositionStyle(),
        {
          opacity: fadeAnim,
          transform: [{ scale: scaleAnim }],
        },
      ]}
    >
      {/* 对话气泡 */}
      <View style={styles.speechBubble}>
        <Text style={styles.messageText}>{message}</Text>
        <TouchableOpacity style={styles.closeButton} onPress={hideTeacher}>
          <Text style={styles.closeButtonText}>×</Text>
        </TouchableOpacity>
      </View>

      {/* 教师形象 */}
      <View style={styles.teacherContainer}>
        <View style={styles.teacherAvatar}>
          {/* 头发 */}
          <View style={styles.hair} />

          {/* 脸部 */}
          <View style={styles.face}>
            {/* 眼睛 */}
            <View style={styles.eyesContainer}>
              <View style={styles.eye}>
                <View style={styles.eyeball} />
              </View>
              <View style={styles.eye}>
                <View style={styles.eyeball} />
              </View>
            </View>

            {/* 嘴巴 */}
            <View style={styles.mouth} />
          </View>

          {/* 身体 */}
          <View style={styles.body}>
            {/* 衬衫 */}
            <View style={styles.shirt} />
            {/* 外套 */}
            <View style={styles.jacket} />
          </View>
        </View>

        {/* 装饰元素 */}
        <View style={styles.sparkle1}>
          <Text style={styles.sparkle1Text}>✨</Text>
        </View>
        <View style={styles.sparkle2}>
          <Text style={styles.sparkle2Text}>💫</Text>
        </View>
      </View>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    alignItems: 'center',
    zIndex: 1000,
  },
  speechBubble: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 12,
    marginBottom: 8,
    maxWidth: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    borderWidth: 2,
    borderColor: '#e8f4fd',
    position: 'relative',
  },
  messageText: {
    fontSize: 14,
    color: '#2c3e50',
    lineHeight: 20,
    textAlign: 'center',
    fontWeight: '500',
  },
  closeButton: {
    position: 'absolute',
    top: -5,
    right: -5,
    backgroundColor: '#ff6b6b',
    borderRadius: 12,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    lineHeight: 16,
  },
  teacherContainer: {
    position: 'relative',
  },
  teacherAvatar: {
    width: 80,
    height: 100,
    alignItems: 'center',
  },
  hair: {
    width: 60,
    height: 35,
    backgroundColor: '#8B4513',
    borderRadius: 30,
    marginBottom: -10,
    borderWidth: 2,
    borderColor: '#654321',
  },
  face: {
    width: 50,
    height: 45,
    backgroundColor: '#FDBCB4',
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#F8A598',
    marginBottom: 5,
  },
  eyesContainer: {
    flexDirection: 'row',
    marginTop: -8,
    marginBottom: 8,
  },
  eye: {
    width: 12,
    height: 8,
    backgroundColor: '#fff',
    borderRadius: 6,
    marginHorizontal: 4,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#333',
  },
  eyeball: {
    width: 6,
    height: 6,
    backgroundColor: '#333',
    borderRadius: 3,
  },
  mouth: {
    width: 8,
    height: 4,
    backgroundColor: '#ff6b9d',
    borderRadius: 4,
  },
  body: {
    width: 45,
    height: 50,
    alignItems: 'center',
  },
  shirt: {
    width: 40,
    height: 35,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  jacket: {
    width: 45,
    height: 25,
    backgroundColor: '#2c3e50',
    borderRadius: 8,
    marginTop: -20,
    borderWidth: 2,
    borderColor: '#1a252f',
  },
  sparkle1: {
    position: 'absolute',
    top: -10,
    left: -15,
  },
  sparkle1Text: {
    fontSize: 16,
  },
  sparkle2: {
    position: 'absolute',
    top: 20,
    right: -20,
  },
  sparkle2Text: {
    fontSize: 12,
  },
});

export default TeacherAvatar;
